import React, { useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Switch, BrowserRouter, useLocation, useHistory } from 'react-router-dom';
import DocumentMeta from 'react-document-meta';
import queryString from 'query-string';
import { UserRoutes, AdminRoutes, CompanyRoutes, MinionRoutes, ToolRoutes } from './router';
import { Provider } from 'react-redux';
import { store } from './Redux/store';
import { FullCoverLoading, FullCoverLoadingWithAnime, ScrollToTop, FlushToaster } from './components';
import { ErrorBoundary } from 'react-error-boundary';
import { useAppDispatch, useSelector } from './Redux/store';
import { setNextToCurrentFlushToasterAction, } from './Redux/slices/flushToasterSlice';
import { hideSidebarAction as userHideSidebarAction } from './Redux/slices/user/sidebarSlice';
import { resetUserAction } from './Redux/slices/user/authSlice';
import { hideSidebarAction as minionHideSidebarAction } from './Redux/slices/minion/sidebarSlice';
import { resetMinionAction } from './Redux/slices/minion/authSlice';
import { hideSidebarAction as companyHideSidebarAction } from './Redux/slices/company/sidebarSlice';
import { resetCompanyAction } from './Redux/slices/company/authSlice';
import { setIsMobileApplicationAction } from './Redux/slices/mobileApplicationSlice';
import { useTracking } from './CustomHooks/useTracking';
const App = () => {
    return (React.createElement(React.Fragment, null,
        React.createElement(Provider, { store: store },
            React.createElement(ErrorBoundary, { FallbackComponent: ErrorFallback },
                React.createElement(FullCoverLoading, null),
                React.createElement(FullCoverLoadingWithAnime, null),
                React.createElement(FlushToaster, null),
                React.createElement(BrowserRouter, null,
                    React.createElement(ScrollToTop, null),
                    React.createElement(Switcher, null))))));
};
const Switcher = () => {
    const location = useLocation();
    const history = useHistory();
    const search = location.search;
    const query = queryString.parse(search);
    const dispatch = useAppDispatch();
    const userId = useSelector(state => state.users.id);
    const companyId = useSelector(state => state.companies.id);
    const minionId = useSelector(state => state.minions.id);
    const flushToasterStore = useSelector(state => state.flushToaster.next);
    const mobileApplicationStore = useSelector(state => state.mobileApplication);
    const meta = {
        title: '現場のヒーロー - 建設業で働く職人のスキルアップをサポートする教育型マッチングサービス',
        description: '現場のヒーローは建設業で働く職人のスキルアップをサポートする教育型マッチングサービスです。初心者でも学べるカリキュラムや現場で使えるツールを用意。スキルアップして希望の仕事を見つけることができます。',
        meta: {
            charset: 'utf-8',
            name: {
                keywords: '現場のヒーロー,配管工7つ道具,配管工,建設,親方,職人,マッチング',
            }
        }
    };
    const g4_tag = process.env.MIX_ANALYTICS_G4_TAG;
    const ua_tag = process.env.MIX_ANALYTICS_UA_TAG;
    // クッキー情報
    const rowCookie = document.cookie;
    // クッキーの名前を指定して値を取得する
    const cookie = useCallback((cookieName) => {
        const array = rowCookie.split(';');
        const found = array.find((value) => {
            const content = value.split('=');
            return cookieName === content[0].trim();
        });
        if (found) {
            const content = found.split('=');
            return content[1];
        }
        return;
    }, [rowCookie]);
    const resetHandler = useCallback(() => {
        /*
        dispatch(
            setNextFlushToasterAction({
                type: ToastTypeEnum.danger,
                title: 'エラー',
                message: '多重ログインを検知しました',
            })
        );
        */
        history.push('/');
    }, []);
    // Cookieでセットされている auth id と　redux の値が異なる場合
    // reduxのauth情報をクリアしてTOPに遷移させる
    useEffect(() => {
        const authUserId = cookie('auth_user_id');
        const authCompanyId = cookie('auth_company_id');
        const authMinionId = cookie('auth_minion_id');
        if (authUserId && userId && Number(authUserId) !== userId) {
            dispatch(resetUserAction());
            resetHandler();
            return;
        }
        if (authCompanyId && companyId && Number(authCompanyId) !== companyId) {
            dispatch(resetCompanyAction());
            resetHandler();
            return;
        }
        if (authMinionId && minionId && Number(authMinionId) !== minionId) {
            dispatch(resetMinionAction());
            resetHandler();
            return;
        }
    }, [cookie, userId, companyId, minionId]);
    useEffect(() => {
        // フラッシュトーストNEXTをCURRENTに置き換え
        dispatch(setNextToCurrentFlushToasterAction());
        // サイドバーを閉じる
        dispatch(userHideSidebarAction());
        dispatch(minionHideSidebarAction());
        dispatch(companyHideSidebarAction());
    }, [location.key]);
    useEffect(() => {
        // アプリケーションの通信かどうかをチェックして保存する
        const queryApl = 'apl' in query ? query['apl'] : '';
        if (queryApl === 'true') {
            dispatch(setIsMobileApplicationAction(true));
        }
        if (queryApl === 'false') {
            dispatch(setIsMobileApplicationAction(false));
        }
    }, [query]);
    if (location.pathname.startsWith('/admin')) {
        return (React.createElement(Switch, null,
            React.createElement(AdminRoutes, null)));
    }
    else if (location.pathname.startsWith('/company')) {
        if (g4_tag)
            useTracking(g4_tag);
        if (ua_tag)
            useTracking(ua_tag);
        return (React.createElement(Switch, null,
            React.createElement(DocumentMeta, Object.assign({}, meta),
                React.createElement(CompanyRoutes, null))));
    }
    else if (location.pathname.startsWith('/minion')) {
        if (g4_tag)
            useTracking(g4_tag);
        if (ua_tag)
            useTracking(ua_tag);
        return (React.createElement(Switch, null,
            React.createElement(DocumentMeta, Object.assign({}, meta),
                React.createElement(MinionRoutes, null))));
    }
    else if (location.pathname.startsWith('/tool')) {
        if (g4_tag)
            useTracking(g4_tag);
        if (ua_tag)
            useTracking(ua_tag);
        return (React.createElement(Switch, null,
            React.createElement(DocumentMeta, Object.assign({}, meta),
                React.createElement(ToolRoutes, null))));
    }
    else {
        if (g4_tag)
            useTracking(g4_tag);
        if (ua_tag)
            useTracking(ua_tag);
        return (React.createElement(Switch, null,
            React.createElement(DocumentMeta, Object.assign({}, meta),
                React.createElement(UserRoutes, null))));
    }
};
const ErrorFallback = ({ error, resetErrorBoundary }) => {
    return (React.createElement("div", { role: "alert" },
        React.createElement("pre", null, error.message),
        React.createElement("button", { className: "px-3 py-1 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-purple-600 border border-transparent rounded-md active:bg-purple-600 hover:bg-purple-700 focus:outline-none focus:shadow-outline-purple", onClick: resetErrorBoundary }, "\u623B\u308B")));
};
if (document.getElementById('app')) {
    ReactDOM.render(React.createElement(App, null), document.getElementById('app'));
}
